import React, { useRef, useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  FilePdf, UploadSimple, Sparkle, Trash, Plus, CheckCircle, Warning,
  Lock, Lightning,
} from '@phosphor-icons/react';
import { parsePoPdf, commitPoPdf, getPoPdfUsage } from '../lib/api';

/**
 * PoPdfImportDialog — drag/drop a PO PDF, AI parses it, user reviews, commits.
 *
 * Three internal phases:
 *  1. "upload"  — drag-drop or file picker, no parse yet.
 *  2. "review"  — show parsed fields + line-item table, user can edit any cell.
 *  3. "done"    — committed, container created, dialog auto-closes.
 *
 * Always renders inside a controlled <Dialog> so the parent decides when it
 * opens. On successful commit we call onCreated(container) so the parent can
 * refresh its list without a full reload.
 */
export default function PoPdfImportDialog({ open, onOpenChange, onCreated }) {
  const fileInput = useRef(null);
  const [phase, setPhase] = useState('upload');
  const [dragOver, setDragOver] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [filename, setFilename] = useState('');
  const [parsed, setParsed] = useState(null);
  const [usage, setUsage] = useState(null); // {plan_key, used, limit, remaining}
  const [paywall, setPaywall] = useState(null); // {error, message, current_plan}

  // Refresh usage stats whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      try {
        const { data } = await getPoPdfUsage();
        if (active) setUsage(data);
      } catch {/* non-blocking */}
    })();
    return () => { active = false; };
  }, [open]);

  const reset = useCallback(() => {
    setPhase('upload');
    setDragOver(false);
    setParsing(false);
    setCommitting(false);
    setFilename('');
    setParsed(null);
    setPaywall(null);
  }, []);

  const handleClose = useCallback((next) => {
    if (!next) reset();
    onOpenChange(next);
  }, [onOpenChange, reset]);

  const onFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please drop a .pdf file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('PDF exceeds 10 MB limit');
      return;
    }
    setFilename(file.name);
    setParsing(true);
    setPaywall(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await parsePoPdf(fd);
      setParsed({
        ...data,
        // ensure controlled inputs get a string fallback
        supplier_name: data.supplier_name || '',
        po_number: data.po_number || '',
        eta: data.eta || '',
        container_number: '',
        notes: '',
        lines: (data.lines || []).map((l) => ({
          description: l.description || '',
          sku_code: l.sku_code || '',
          expected_qty: l.expected_qty || 0,
          unit_cost: l.unit_cost ?? '',
          matched_sku_id: l.matched_sku_id || null,
          match_confidence: l.match_confidence || 0,
        })),
      });
      if (data.usage) setUsage((u) => ({ ...(u || {}), ...data.usage }));
      setPhase('review');
      toast.success(`Parsed ${data.lines?.length || 0} line items from ${file.name}`);
    } catch (e) {
      // 402 → upgrade or quota — surface as in-dialog paywall instead of a toast
      if (e?.response?.status === 402 && e.response.data?.detail) {
        setPaywall(e.response.data.detail);
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to parse PDF');
      }
      setFilename('');
    } finally {
      setParsing(false);
    }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  }, [onFile]);

  const updateLine = (i, patch) => {
    setParsed((p) => ({
      ...p,
      lines: p.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)),
    }));
  };
  const removeLine = (i) => {
    setParsed((p) => ({ ...p, lines: p.lines.filter((_, idx) => idx !== i) }));
  };
  const addLine = () => {
    setParsed((p) => ({
      ...p,
      lines: [...p.lines, {
        description: '', sku_code: '', expected_qty: 0,
        unit_cost: '', matched_sku_id: null, match_confidence: 0,
      }],
    }));
  };

  const handleCommit = async () => {
    if (!parsed?.supplier_name?.trim()) {
      toast.error('Supplier name is required');
      return;
    }
    if (!parsed.lines.length) {
      toast.error('At least one line item is required');
      return;
    }
    setCommitting(true);
    try {
      const payload = {
        supplier_name: parsed.supplier_name.trim(),
        po_number: parsed.po_number?.trim() || null,
        eta: parsed.eta || null,
        container_number: parsed.container_number?.trim() || null,
        notes: parsed.notes?.trim() || null,
        lines: parsed.lines.map((l) => ({
          description: l.description || null,
          sku_code: l.sku_code || null,
          expected_qty: parseInt(l.expected_qty || 0, 10) || 0,
          unit_cost: l.unit_cost === '' || l.unit_cost == null ? null : parseFloat(l.unit_cost),
          matched_sku_id: l.matched_sku_id || null,
          match_confidence: l.match_confidence || 0,
        })),
      };
      const { data } = await commitPoPdf(payload);
      toast.success(`Container ${data.container_number} created`);
      onCreated?.(data);
      handleClose(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create container');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="border border-border rounded-none max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="po-pdf-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkle size={22} weight="fill" className="text-brand" /> Import PO from PDF
            <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-amber-100 text-amber-800 rounded-none" data-testid="po-pdf-pro-badge">
              <Lightning size={10} weight="fill" /> PRO
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Drop a Purchase Order PDF — Stockaris uses AI to extract line items, then you review and confirm.
          </DialogDescription>
        </DialogHeader>

        {/* Usage meter — visible whenever we have it (Pro & Trial only have limits) */}
        {usage && phase === 'upload' && !paywall && (
          <div className="flex items-center justify-between gap-3 px-3 py-2 bg-muted/50 border border-border" data-testid="po-pdf-usage-meter">
            <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              {usage.plan_key} plan
            </span>
            <span className="text-xs tabular-nums font-mono" data-testid="po-pdf-usage-text">
              {usage.limit == null ? (
                <span className="text-emerald-700 font-bold">Unlimited this month</span>
              ) : (
                <>
                  <span className={`font-bold ${usage.remaining === 0 ? 'text-rose-600' : 'text-foreground'}`}>{usage.used}</span>
                  <span className="text-muted-foreground"> / {usage.limit} PDFs this month</span>
                </>
              )}
            </span>
          </div>
        )}

        {/* Paywall — shown when backend returns 402 (upgrade_required or quota_exceeded) */}
        {paywall && (
          <div className="border border-amber-300 bg-amber-50 p-6 text-center" data-testid="po-pdf-paywall">
            <Lock size={42} weight="duotone" className="mx-auto text-amber-600 mb-3" />
            <p className="text-base font-bold tracking-tight text-amber-900 mb-1">
              {paywall.error === 'quota_exceeded' ? 'Monthly limit reached' : 'Upgrade required'}
            </p>
            <p className="text-xs text-amber-800 max-w-md mx-auto mb-4">{paywall.message}</p>
            {paywall.error === 'quota_exceeded' && (
              <p className="text-[11px] uppercase tracking-widest font-bold text-amber-700 mb-4" data-testid="po-pdf-paywall-usage">
                {paywall.used} / {paywall.limit} PDFs · resets next month
              </p>
            )}
            <div className="flex justify-center gap-2">
              <Button
                onClick={() => { setPaywall(null); reset(); }}
                variant="outline"
                className="rounded-none"
                data-testid="po-pdf-paywall-dismiss"
              >
                Cancel
              </Button>
              <Button
                onClick={() => { window.location.href = paywall.upgrade_url || '/billing'; }}
                className="bg-brand hover:bg-brand/90 rounded-none"
                data-testid="po-pdf-paywall-upgrade"
              >
                <Lightning size={14} weight="fill" className="mr-1" /> Upgrade plan
              </Button>
            </div>
          </div>
        )}

        {phase === 'upload' && !paywall && (
          <div
            data-testid="po-pdf-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => !parsing && fileInput.current?.click()}
            className={`border-2 border-dashed rounded-none p-12 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-brand bg-brand/5' : 'border-border bg-muted/30 hover:bg-muted/50'
            } ${parsing ? 'opacity-60 pointer-events-none' : ''}`}
          >
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="po-pdf-file-input"
            />
            <FilePdf size={56} weight="duotone" className="mx-auto text-brand mb-3" />
            {parsing ? (
              <>
                <p className="text-base font-bold tracking-tight">Parsing {filename}…</p>
                <p className="text-xs text-muted-foreground mt-1">Gemini 3 Flash · usually 5-15 seconds</p>
              </>
            ) : (
              <>
                <p className="text-base font-bold tracking-tight">Drop a PDF here, or click to browse</p>
                <p className="text-xs text-muted-foreground mt-1">Any supplier · scanned or text · max 10 MB</p>
                <Button className="bg-brand hover:bg-brand/90 rounded-none mt-4" data-testid="po-pdf-pick-btn">
                  <UploadSimple size={16} className="mr-2" /> Choose PDF
                </Button>
              </>
            )}
          </div>
        )}

        {phase === 'review' && parsed && (
          <div className="space-y-4" data-testid="po-pdf-review">
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-none">
              <CheckCircle size={18} weight="fill" className="text-emerald-600" />
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">
                Parsed {parsed.lines.length} lines from {filename}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Supplier *</Label>
                <Input
                  value={parsed.supplier_name}
                  onChange={(e) => setParsed({ ...parsed, supplier_name: e.target.value })}
                  data-testid="po-pdf-supplier-input"
                />
              </div>
              <div>
                <Label className="overline">PO Number</Label>
                <Input
                  value={parsed.po_number}
                  onChange={(e) => setParsed({ ...parsed, po_number: e.target.value })}
                  data-testid="po-pdf-ponumber-input"
                />
              </div>
              <div>
                <Label className="overline">Container # (optional)</Label>
                <Input
                  value={parsed.container_number}
                  onChange={(e) => setParsed({ ...parsed, container_number: e.target.value })}
                  placeholder="auto-generated from PO #"
                  data-testid="po-pdf-container-input"
                />
              </div>
              <div>
                <Label className="overline">ETA</Label>
                <Input
                  type="date"
                  value={parsed.eta?.slice(0, 10) || ''}
                  onChange={(e) => setParsed({ ...parsed, eta: e.target.value })}
                  data-testid="po-pdf-eta-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="overline">Line Items</Label>
                <Button size="sm" variant="outline" onClick={addLine} data-testid="po-pdf-add-line-btn" className="rounded-none">
                  <Plus size={14} className="mr-1" /> Add line
                </Button>
              </div>
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-2 font-bold uppercase tracking-widest">SKU</th>
                      <th className="text-left p-2 font-bold uppercase tracking-widest">Description</th>
                      <th className="text-right p-2 font-bold uppercase tracking-widest w-24">Qty</th>
                      <th className="text-right p-2 font-bold uppercase tracking-widest w-28">Unit Cost</th>
                      <th className="text-center p-2 font-bold uppercase tracking-widest w-20">Match</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.lines.map((l, i) => (
                      <tr key={i} className="border-t border-border" data-testid={`po-pdf-line-${i}`}>
                        <td className="p-1">
                          <Input
                            value={l.sku_code || ''}
                            onChange={(e) => updateLine(i, { sku_code: e.target.value })}
                            className="h-8 rounded-none text-xs font-mono"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={l.description || ''}
                            onChange={(e) => updateLine(i, { description: e.target.value })}
                            className="h-8 rounded-none text-xs"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="number"
                            value={l.expected_qty}
                            onChange={(e) => updateLine(i, { expected_qty: e.target.value })}
                            className="h-8 rounded-none text-xs text-right tabular-nums"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={l.unit_cost ?? ''}
                            onChange={(e) => updateLine(i, { unit_cost: e.target.value })}
                            className="h-8 rounded-none text-xs text-right tabular-nums"
                          />
                        </td>
                        <td className="p-1 text-center">
                          {l.match_confidence >= 0.99 ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-700" title="Exact SKU match">
                              <CheckCircle size={12} weight="fill" /> Exact
                            </span>
                          ) : l.match_confidence >= 0.5 ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700" title="Fuzzy description match — verify">
                              <Warning size={12} weight="fill" /> Fuzzy
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold uppercase text-muted-foreground">New</span>
                          )}
                        </td>
                        <td className="p-1 text-center">
                          <button
                            type="button"
                            onClick={() => removeLine(i)}
                            className="text-muted-foreground hover:text-rose-600"
                            data-testid={`po-pdf-remove-line-${i}`}
                            aria-label="Remove line"
                          >
                            <Trash size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!parsed.lines.length && (
                      <tr>
                        <td colSpan={6} className="p-4 text-center text-muted-foreground text-xs">No line items — add one to continue.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <Label className="overline">Notes (optional)</Label>
              <Input
                value={parsed.notes}
                onChange={(e) => setParsed({ ...parsed, notes: e.target.value })}
                placeholder="Imported from PDF"
                data-testid="po-pdf-notes-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="outline" onClick={() => { reset(); }} className="rounded-none" data-testid="po-pdf-restart-btn">
                Upload different PDF
              </Button>
              <Button
                onClick={handleCommit}
                disabled={committing}
                className="bg-brand hover:bg-brand/90 rounded-none"
                data-testid="po-pdf-commit-btn"
              >
                {committing ? 'Creating…' : 'Create Container'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
